import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {NgxMaskDirective} from 'ngx-mask';
import {InputErrorComponent} from '../input-error/input-error.component';

@Component({
    imports: [CommonModule, InputErrorComponent, ReactiveFormsModule, NgxMaskDirective],
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true
})
export class InputComponent {
  @Input() form!: UntypedFormGroup;
  @Input() name!: string;
  @Input() readonly!: boolean;
  @Input() label!: string;
  @Input() type: string = 'text';
  @Input() submitted: boolean = false;
  @Input() isCurrency: boolean = false;
  @Input() isPercentage: boolean = false;

  getFormControl(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  formControlHasError(name: string): boolean | null {
    const control = this.getFormControl(name);
    return control.invalid && control.errors && (control.dirty || control.touched || this.submitted);
  }

  hasValue(name: string): boolean {
    return this.getFormControl(name).value;
  }

  onCurrencyKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;

    if (this.parseCurrency(this.ctrl.value) !== 0) return;

    event.preventDefault();
    this.ctrl.setValue(null);
    (event.target as HTMLInputElement).value = '';
  }

  onCurrencyBlur(): void {
    const num = this.parseCurrency(this.ctrl.value);
    if (num != null) {
      this.ctrl.setValue(num.toFixed(2));
    }
  }

  onCurrencyInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.isCursorInDecimalPart(input)) return;

    this.padDecimals(input);
  }

  private get ctrl(): UntypedFormControl {
    return this.form.get(this.name) as UntypedFormControl;
  }

  private parseCurrency(value: unknown): number | null {
    if (value == null || value === '') return null;
    const str = typeof value === 'string' ? value.replace(/'/g, '') : String(value);
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  }

  private isCursorInDecimalPart(input: HTMLInputElement): boolean {
    const cursorPos = input.selectionStart ?? 0;
    const dotPos = input.value.indexOf('.');
    return dotPos > -1 && cursorPos > dotPos;
  }

  // ngx-mask doesn't pad decimals, so we have to do it ourselves'
  private padDecimals(input: HTMLInputElement): void {
    const num = this.parseCurrency(this.ctrl.value);
    if (num == null || num === 0) return;

    const alreadyFormatted = /\.\d{2}$/.test(String(this.ctrl.value));
    if (alreadyFormatted) return;

    this.ctrl.setValue(num.toFixed(2));
    this.placeCursorBeforeDecimals(input);
  }

  private placeCursorBeforeDecimals(input: HTMLInputElement): void {
    requestAnimationFrame(() => {
      const dotPos = input.value.indexOf('.');
      if (dotPos > -1) {
        input.setSelectionRange(dotPos, dotPos);
      }
    });
  }
}
